import React from 'react';
import { Box, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { ChromePicker } from 'react-color';
import ActionButton from '../../../components/ActionButton';
import BrandNameInput from './BrandNameInput';
import BrandUrlField from './BrandUrlField';
import ColorPickerToggle from './ColorPickerToggle';
import LoghiTemplate from './LoghiTemplate';
import LogoHalf from './LogoHalf';
import LogoHeaderTemplate from './LogoHeaderTemplate';
import { LOGO_HALF_MAX_WIDTH } from './logoSectionLayout';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '50px',
    gap: '24px',
  },
  paper: {
    width: '890px',
    padding: '12px',
    boxShadow: theme.shadows[3],
  },
  pickerWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: ' center',
    margin: '30px 0',
    gap: '35px',
  },
  textWrapper: {
    marginTop: '30px',
  },
  text: {
    margin: '5px 0px ',
  },
  titoloSezione: {
    fontWeight: theme.typography.fontWeightBold,
  },
  loghiWrapper: {
    // The two halves each contribute a heading, an upload box and a block of
    // specifications. Those three sit in rows owned here rather than in each
    // half, so a description that wraps to two lines on one side still leaves
    // both upload boxes on the same line. Each LogoHalf opts its own parts
    // into these rows; where the browser cannot, the halves simply lay
    // themselves out and lose the alignment, nothing else.
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
    justifyItems: 'center',
    // mainContainer centres its children, which would shrink this grid to its
    // content and leave auto-fit only one column to work with.
    width: '100%',
    // Two halves at their widest. Past that the pair would drift apart towards
    // the edges of the window, each half centred in its own half of the row,
    // while the panel below stays put.
    maxWidth: `calc(2 * ${LOGO_HALF_MAX_WIDTH})`,
    // Mirrors the space textWrapper puts above the section heading, so the
    // logo section is set off by the same amount at both ends rather than
    // running into the panel below it.
    marginBottom: '30px',
  },
  buttonWrapper: {
    padding: '20px 0',
  },
}));

const DettagliWebApp = ({
  isEditMode,
  brandName,
  handleBrandNameChange,
  templateData,
  handlePathChange,
  loghiTemplate,
  uploadImage,
  logoLeftUrl,
  uploadLogoLeft,
  selectedColor,
  handleColorChange,
  handleBackClick,
  createOrUpdateBrand,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.mainContainer}>
      <BrandNameInput
        isEditMode={isEditMode}
        brandName={brandName}
        handleBrandNameChange={handleBrandNameChange}
      />
      <BrandUrlField
        url={templateData.path}
        onUrlChange={handlePathChange}
        isEditMode={isEditMode}
      />
      <div className={classes.textWrapper}>
        <Typography
          variant="h5"
          component="h2"
          className={`${classes.text} ${classes.titoloSezione}`}
        >
          Logo del brand
        </Typography>
      </div>
      <div className={classes.loghiWrapper}>
        <LogoHalf
          titolo="Principale"
          descrizione="mostrato sulla pagina di accesso e nell'area pagamenti."
          consiglioRapporto="Per risultati ottimali si consiglia un rapporto d'aspetto compreso tra 16:9 e 9:16."
        >
          <LoghiTemplate loghiIds={loghiTemplate} onSubmit={uploadImage} />
        </LogoHalf>
        <LogoHalf
          titolo="Thumbnail"
          descrizione="mostrato in alto a sinistra su ogni pagina e usato come icona della scheda del browser."
          consiglioRapporto="Per risultati ottimali si consiglia un'immagine quadrata (1:1)."
        >
          <LogoHeaderTemplate
            logoHeader={logoLeftUrl}
            uploadLogoHeader={uploadLogoLeft}
          />
        </LogoHalf>
      </div>
      <Paper className={classes.paper}>
        <div className={classes.pickerWrapper}>
          <Typography variant="h5" style={{ fontWeight: 'bold' }}>
            Colori del brand
          </Typography>
          <Typography>
            Seleziona i due colori di riferimento della tua attività
          </Typography>
          <ColorPickerToggle
            selectedColor={selectedColor}
            onColorChange={handleColorChange}
          />
        </div>
        <div className={classes.pickerWrapper}>
          <Typography>Seleziona il colore del testo</Typography>
          <ChromePicker
            color={selectedColor.text}
            onChangeComplete={(color) => handleColorChange('text', color)}
            disableAlpha
          />
        </div>
      </Paper>
      <div className={classes.buttonWrapper}>
        <ActionButton
          label="Indietro"
          secondaryButton
          onClick={handleBackClick}
          grayVersion={false}
          disabled={false}
        />
        <ActionButton
          label={isEditMode ? 'Salva modifiche' : 'Crea brand'}
          onClick={createOrUpdateBrand}
          grayVersion={false}
        />
      </div>
    </Box>
  );
};

DettagliWebApp.propTypes = {
  isEditMode: PropTypes.bool.isRequired,
  brandName: PropTypes.string.isRequired,
  handleBrandNameChange: PropTypes.func.isRequired,
  templateData: PropTypes.object.isRequired,
  handlePathChange: PropTypes.func.isRequired,
  loghiTemplate: PropTypes.array.isRequired,
  uploadImage: PropTypes.func.isRequired,
  logoLeftUrl: PropTypes.string.isRequired,
  uploadLogoLeft: PropTypes.func.isRequired,
  selectedColor: PropTypes.object.isRequired,
  handleColorChange: PropTypes.func.isRequired,
  handleBackClick: PropTypes.func.isRequired,
  createOrUpdateBrand: PropTypes.func.isRequired,
};

export default DettagliWebApp;
